import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import * as Yup from "yup"
import { DialogContent, Grid } from "@mui/material"
import { Button, InputText, Typography } from "@synapse-analytics/synapse-ui"
import { useFormik } from "formik"
import { t } from "i18next"

import { SideBar } from "../../components/UI/SideBar"
import { InfoBlock } from "../../components/containers/InfoBlock"
import type { RootState } from "../../store/ReduxStore"
import { ProductEnum } from "../../types/generated/authentication/ProductEnum"
import type { RolePermission } from "../../types/generated/authentication/RolePermission"
import {
  type RestructuredPermissionsMap,
  getDefaultCheckedPermissionValues,
  restructurePermissions,
  useIsPermitted,
} from "../../utils/PermissionsHelpers"
import { getRoleDisplayName } from "../../utils/rolesHelpers"
import type { RoleDialogProps } from "./Interfaces"
import { PermissionAccordion } from "./PermissionsAccordion"

import styles from "./Roles.module.scss"

const PermissionsRenderer = React.memo<{
  permissions: RestructuredPermissionsMap
  checkedPermissions: string[]
  setCheckedPermissions: (permissions: string[]) => void
  isAdmin: boolean
  isReadMode: boolean
  keyPrefix?: string
}>(({ permissions, checkedPermissions, setCheckedPermissions, isAdmin, isReadMode, keyPrefix = "" }) => (
  <PermissionAccordion
    permissions={permissions}
    checkPermissions={checkedPermissions}
    setCheckedPermissions={setCheckedPermissions}
    isAdmin={isAdmin}
    isReadMode={isReadMode}
    keyPrefix={keyPrefix}
  />
))

PermissionsRenderer.displayName = "PermissionsRenderer"

// Yup validation schema object
const validationSchema = Yup.object({
  roleName: Yup.string().required(t("RoleCreationDialog.validation.roleNameRequired", { ns: "settings" })),
  description: Yup.string(),
})

/**
 * Role creation/edit dialog
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {boolean} isEdit
 * @returns {React.ReactElement}
 */
export function RoleCreationDialog(props: Readonly<RoleDialogProps>): React.ReactElement {
  const { isOpen, onClose, role, onAccept, isViewMode } = props

  const { t } = useTranslation("settings")

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const sidPermissions = useSelector((state: RootState) => state.permissions.sidPermissions)
  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)
  const konanPermissions = useSelector((state: RootState) => state.permissions.konanPermissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canUpdateRoles = useIsPermitted("Update roles", permissions.synapse_id, flattenedSidPermissions)

  const [isReadMode, setIsReadMode] = useState<boolean>(isViewMode ?? false)

  const isAdmin = role?.name === "OWNER"

  const sidPermissionsChildren = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const children = (sidPermissions as { "0": { children: any } })?.["0"]?.children || {}
    return restructurePermissions(children)
  }, [sidPermissions])

  const konanPermissionsRestructured = useMemo(() => {
    return restructurePermissions(konanPermissions)
  }, [konanPermissions])

  const formik = useFormik({
    initialValues: {
      roleName: role?.name ?? "",
      description: role?.description ?? "",
      konanCheckedPermissions: [] as string[],
      sidCheckedPermissions: [] as string[],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Extract the actual permission IDs by removing the prefix
      const konanPermissions = values.konanCheckedPermissions
        .filter((perm) => perm.startsWith("konan-"))
        .map((perm) => perm.replace("konan-", ""))

      const sidPermissions = values.sidCheckedPermissions
        .filter((perm) => perm.startsWith("sid-"))
        .map((perm) => perm.replace("sid-", ""))

      await onAccept?.(values.roleName, values.description, [
        ...konanPermissions.map((permission: string): RolePermission => {
          return { permission: parseInt(permission), product: ProductEnum.KONAN }
        }),
        ...sidPermissions.map((permission: string): RolePermission => {
          return { permission: parseInt(permission), product: ProductEnum.SYNAPSE_ID }
        }),
      ])

      resetForm({})
    },
  })

  const roleDisplayName = getRoleDisplayName(formik.values.roleName)

  const handleSidPermissionsChange = useCallback(
    (permissions: string[]) => {
      if (!isReadMode) {
        formik.setFieldValue("sidCheckedPermissions", permissions)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReadMode, formik.setFieldValue],
  )

  const handleKonanPermissionsChange = useCallback(
    (permissions: string[]) => {
      if (!isReadMode) {
        formik.setFieldValue("konanCheckedPermissions", permissions)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReadMode, formik.setFieldValue],
  )

  const handleClose = useCallback(() => {
    onClose()
    formik.resetForm({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, formik.resetForm])

  const handleUnselectAll = useCallback(() => {
    formik.setFieldValue("sidCheckedPermissions", [])
    formik.setFieldValue("konanCheckedPermissions", [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.setFieldValue])

  const shouldExpandRoleName = isReadMode && formik.values.description.length === 0

  const totalSelectedPermissions =
    formik.values.konanCheckedPermissions.length + formik.values.sidCheckedPermissions.length

  const hasSelectedPermissions = useMemo(
    () => formik.values.sidCheckedPermissions.length > 0 || formik.values.konanCheckedPermissions.length > 0,
    [formik.values.sidCheckedPermissions.length, formik.values.konanCheckedPermissions.length],
  )

  // Memoize actions array
  const actions = useMemo(() => {
    const actions = []

    if (!isReadMode) {
      actions.push(
        <Button key="cancel" variant="secondary" onClick={handleClose}>
          {t("roleCreationDialog.buttons.cancel")}
        </Button>,
        <Button
          onClick={formik.submitForm}
          disabled={formik.isSubmitting || !(formik.isValid && formik.dirty)}
          variant="primary"
          isLoading={formik.isSubmitting}
        >
          {t("roleCreationDialog.buttons.save")}
        </Button>,
      )
    }

    if (isReadMode && !["OWNER", "USER"].includes(role?.name ?? "") && canUpdateRoles) {
      actions.push(
        <Button onClick={() => setIsReadMode(false)} variant="primary">
          {t("roleCreationDialog.buttons.edit")}
        </Button>,
      )
    }

    return actions
  }, [
    handleClose,
    t,
    isReadMode,
    canUpdateRoles,
    role?.name,
    formik.submitForm,
    formik.isSubmitting,
    formik.isValid,
    formik.dirty,
  ])

  // Set existing Permissions on mount
  useEffect(() => {
    if (role?.name !== "USER") {
      if (role?.permissions) {
        const tempKonanPermissions: string[] = []
        const tempSIDPermissions: string[] = []

        for (const permission of role.permissions) {
          if (permission.product === ProductEnum.KONAN) {
            // Add prefix when setting initial values
            tempKonanPermissions.push(`konan-${permission.permission.toString()}`)
          } else {
            // Add prefix when setting initial values
            tempSIDPermissions.push(`sid-${permission.permission.toString()}`)
          }
        }

        formik.setFieldValue("konanCheckedPermissions", tempKonanPermissions)
        formik.setFieldValue("sidCheckedPermissions", tempSIDPermissions)
      }
    } else {
      // if role is default user role set default permissions
      const flattenedSid = flattenedSidPermissions
      const flattenedKonan = flattenedKonanPermissions

      formik.setFieldValue("sidCheckedPermissions", getDefaultCheckedPermissionValues(flattenedSid, "sid"))
      formik.setFieldValue("konanCheckedPermissions", getDefaultCheckedPermissionValues(flattenedKonan, "konan"))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role?.name, role?.permissions, flattenedSidPermissions, flattenedKonanPermissions])

  return (
    <SideBar
      title={
        role
          ? isReadMode
            ? t("roleCreationDialog.dialogTitle.view")
            : t("roleCreationDialog.dialogTitle.edit")
          : t("roleCreationDialog.dialogTitle.new")
      }
      isOpen={isOpen}
      onClose={handleClose}
      closeIcon={{
        icon: "close",
        position: "right",
      }}
      variant="floating"
      paperStyle={{
        width: "600px",
      }}
      actions={actions}
    >
      <DialogContent className={styles.creationDialogContent}>
        <Grid container gap={3}>
          <Grid container item xs={12} gap={1} wrap="nowrap">
            <Grid item xs={shouldExpandRoleName ? 12 : 4}>
              {isReadMode ? (
                <InfoBlock
                  text={roleDisplayName}
                  title={t("roleCreationDialog.formFields.roleName.label")}
                  type="default"
                  variant="base"
                />
              ) : (
                <InputText
                  label={t("roleCreationDialog.formFields.roleName.label")}
                  id="roleName"
                  placeholder={t("roleCreationDialog.formFields.roleName.placeholder")}
                  description={t("roleCreationDialog.formFields.roleName.description")}
                  value={roleDisplayName}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  fullWidth
                  required
                />
              )}
            </Grid>

            {!shouldExpandRoleName && (
              <Grid item xs={8}>
                {isReadMode ? (
                  <InfoBlock
                    text={formik.values.description}
                    title={t("roleCreationDialog.formFields.description.label")}
                    type="default"
                  />
                ) : (
                  <InputText
                    label={t("roleCreationDialog.formFields.description.label")}
                    id="description"
                    placeholder={t("roleCreationDialog.formFields.description.placeholder")}
                    value={formik.values.description}
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    fullWidth
                  />
                )}
              </Grid>
            )}
          </Grid>

          <Grid container item xs={12} gap={1}>
            <Grid item xs={12}>
              {isReadMode ? (
                <Typography variant="label-regular" variantColor={2}>
                  {t("roleCreationDialog.permissions.viewTitle")}
                </Typography>
              ) : (
                <Grid item xs={12} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                  <Typography variant={"h3-bold"}>{t("roleCreationDialog.permissions.title")}</Typography>
                  <Grid item display={"flex"} alignItems={"center"} gap={1}>
                    <Button variant="ghost" onClick={handleUnselectAll} disabled={!hasSelectedPermissions}>
                      {t("roleCreationDialog.buttons.unselectAll")}
                    </Button>
                    <Typography variant={"label-bold"}>
                      {totalSelectedPermissions} {t("roleCreationDialog.labels.selected")}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Grid item className={"scrollbar"} style={{ overflowY: "scroll", maxHeight: "60vh" }} pr={1}>
              <PermissionsRenderer
                permissions={sidPermissionsChildren}
                checkedPermissions={formik.values.sidCheckedPermissions}
                setCheckedPermissions={handleSidPermissionsChange}
                isAdmin={isAdmin}
                isReadMode={isReadMode}
                keyPrefix="sid"
              />

              <PermissionsRenderer
                permissions={konanPermissionsRestructured}
                checkedPermissions={formik.values.konanCheckedPermissions}
                setCheckedPermissions={handleKonanPermissionsChange}
                isAdmin={isAdmin}
                isReadMode={isReadMode}
                keyPrefix="konan"
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </SideBar>
  )
}
